import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['input', 'results', 'selected', 'clearButton', 'template']

  connect() {
    this.selectedIndex = -1
    this.items = []
    this.debounceTimer = null
    this.abortController = null
    this.toggleClearButton(this.selectedIds().length > 0)
  }

  disconnect() {
    this.cancelDebounce()
    this.abortFetch()
  }

  search() {
    this.cancelDebounce()
    this.debounceTimer = setTimeout(() => this.performSearch(), 250)
  }

  async performSearch() {
    const query = this.inputTarget.value.trim()

    if (query.length < 1) {
      this.items = []
      this.hideResults()
      return
    }

    this.showLoading()

    try {
      this.abortFetch()
      this.abortController = new AbortController()

      const url = new URL(this.searchUrl, window.location.origin)
      url.searchParams.set('q', query)

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Search failed')

      const selected = new Set(this.selectedIds())
      this.items = (await response.json()).filter((item) => !selected.has(String(item.id)))
      this.selectedIndex = -1
      this.renderResults()
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.renderError()
      }
    }
  }

  renderResults() {
    if (this.items.length === 0) {
      this.resultsTarget.innerHTML = '<li class="film-typeahead__empty">Ничего не найдено</li>'
    } else {
      this.resultsTarget.innerHTML = this.items.map((item, index) => {
        const activeClass = index === this.selectedIndex ? ' film-typeahead__item--active' : ''
        const meta = item.country ? `<div class="film-typeahead__item-meta">${this.escapeHtml(item.country)}</div>` : ''
        return `<li class="film-typeahead__item${activeClass}"
          data-action="mousedown->admin--city-typeahead#selectItem"
          data-index="${index}">
          <div class="film-typeahead__item-name">${this.escapeHtml(item.name_ru)}</div>
          ${meta}
        </li>`
      }).join('')
    }

    this.showResults()
  }

  selectItem(event) {
    event.preventDefault()

    const item = this.items[parseInt(event.currentTarget.dataset.index, 10)]
    if (item) this.select(item)
  }

  select(item) {
    if (this.selectedIds().includes(String(item.id))) {
      this.inputTarget.value = ''
      this.hideResults()
      return
    }

    const chip = document.createElement('span')
    chip.className = 'city-typeahead__chip'
    chip.innerHTML = `
      <input type="hidden" name="${this.escapeHtml(this.paramName)}" value="${this.escapeHtml(String(item.id))}">
      <span>${this.escapeHtml(item.name_ru)}</span>
      <button type="button" class="city-typeahead__chip-remove" data-action="admin--city-typeahead#remove" aria-label="Удалить">&times;</button>
    `
    this.selectedTarget.appendChild(chip)

    this.inputTarget.value = ''
    this.items = []
    this.hideResults()
    this.toggleClearButton(true)
    this.submitForm()
  }

  remove(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest('.city-typeahead__chip')
    if (chip) chip.remove()
    this.toggleClearButton(this.selectedIds().length > 0)
    this.submitForm()
  }

  clear(event) {
    event.preventDefault()

    this.selectedTarget.innerHTML = ''
    this.inputTarget.value = ''
    this.items = []
    this.hideResults()
    this.toggleClearButton(false)
    this.submitForm()
  }

  navigate(event) {
    if (event.key === 'Backspace' && this.inputTarget.value === '' && this.selectedIds().length > 0) {
      const chips = this.selectedTarget.querySelectorAll('.city-typeahead__chip')
      const last = chips[chips.length - 1]
      if (last) {
        last.remove()
        this.toggleClearButton(this.selectedIds().length > 0)
        this.submitForm()
      }
      return
    }

    if (!this.isResultsVisible() || this.items.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.items.length - 1)
        this.renderResults()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.renderResults()
        break
      case 'Enter':
        event.preventDefault()
        if (this.selectedIndex >= 0 && this.items[this.selectedIndex]) {
          this.select(this.items[this.selectedIndex])
        }
        break
      case 'Escape':
        this.hideResults()
        break
    }
  }

  showOnFocus() {
    if (this.inputTarget.value.trim().length >= 1 && this.items.length > 0) {
      this.showResults()
    }
  }

  hideResultsDelayed() {
    setTimeout(() => this.hideResults(), 200)
  }

  showResults() {
    this.resultsTarget.hidden = false
    this.element.classList.add('film-typeahead--open')
  }

  hideResults() {
    this.resultsTarget.hidden = true
    this.element.classList.remove('film-typeahead--open')
  }

  showLoading() {
    this.resultsTarget.innerHTML = '<li class="film-typeahead__empty">Поиск…</li>'
    this.showResults()
  }

  renderError() {
    this.resultsTarget.innerHTML = '<li class="film-typeahead__empty">Ошибка поиска</li>'
    this.showResults()
  }

  toggleClearButton(visible) {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.hidden = !visible
    }
  }

  selectedIds() {
    return Array.from(this.selectedTarget.querySelectorAll(`input[name="${this.paramName}"]`)).map((input) => input.value)
  }

  isResultsVisible() {
    return !this.resultsTarget.hidden
  }

  submitForm() {
    const form = this.element.closest('form')
    if (form) form.submit()
  }

  get searchUrl() {
    return this.data.get('searchUrl') || '/admin/cities/search'
  }

  get paramName() {
    return this.data.get('paramName') || 'city_ids[]'
  }

  cancelDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  abortFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}
